import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import { currentLocale } from '../i18n';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import '../styles/url-lists.css';
import '../styles/reports.css';

interface RemarketingList {
  id: string;
  advertiser_id: string;
  name: string;
  description: string;
  life_span: number;
  list_size: number;
  activity_id: string | null;
  activity_ids: string[] | null;
  activity_name: string | null;
  active: boolean;
  list_source: string;
  remote_id: string | null;
  is_locked?: boolean;
  created_at: string;
  updated_at: string;
}

interface ConversionActivity {
  id: string;
  name: string;
  type: string;
  event_count?: number;
}

// Capacity Gauge Component
const CapacityGauge: React.FC<{
  estimatedUsers: number;
  maxCapacity: number;
  lifeSpan: number;
}> = ({ estimatedUsers, maxCapacity, lifeSpan }) => {
  const { t } = useTranslation();
  const percentage = Math.min((estimatedUsers / maxCapacity) * 100, 100);
  const rotation = (percentage / 100) * 180;

  const formatNumber = (num: number) => {
    if (num < 100) return '<100';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toLocaleString();
  };

  const getCapacityLevel = () => {
    if (percentage < 30) return { label: t('rm.levelBuilding'), color: '#FF6B4A' };
    if (percentage < 60) return { label: t('rm.levelGrowing'), color: '#E94B6C' };
    if (percentage < 85) return { label: t('rm.levelStrong'), color: '#8E3A9A' };
    return { label: t('rm.levelMaximum'), color: '#6B21A8' };
  };

  const level = getCapacityLevel();

  return (
    <div className="capacity-gauge-container">
      <div className="capacity-gauge">
        <svg viewBox="0 0 200 110" className="gauge-svg">
          {/* Background arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="rgba(0,0,0,0.06)"
            strokeWidth="12"
            strokeLinecap="round"
          />
          {/* Gradient definition */}
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF6B4A" />
              <stop offset="50%" stopColor="#E94B6C" />
              <stop offset="100%" stopColor="#8E3A9A" />
            </linearGradient>
          </defs>
          {/* Progress arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={`${(percentage / 100) * 251.2} 251.2`}
            style={{ transition: 'stroke-dasharray 0.6s ease-out' }}
          />
          {/* Fixed end markers */}
          <circle cx="20" cy="100" r="5" fill="#FF6B4A" />
          <circle cx="180" cy="100" r="5" fill="#8E3A9A" />
          {/* Needle */}
          <g transform={`rotate(${rotation - 90}, 100, 100)`} style={{ transition: 'transform 0.6s ease-out' }}>
            <circle cx="100" cy="100" r="6" fill={level.color} />
            <line x1="100" y1="100" x2="100" y2="40" stroke={level.color} strokeWidth="3" strokeLinecap="round" />
          </g>
        </svg>
        <div className="gauge-center">
          <span className="gauge-value">{formatNumber(estimatedUsers)}</span>
          <span className="gauge-label">{t('rm.estimatedUsers')}</span>
        </div>
      </div>
      <div className="capacity-meta">
        <div className="capacity-level">
          <span className="level-dot" style={{ background: level.color }}></span>
          <span className="level-text">{level.label}</span>
        </div>
        <div className="capacity-info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v6l4 2"/>
          </svg>
          <span>{t('rm.retention', { count: lifeSpan })}</span>
        </div>
      </div>
    </div>
  );
};

// Look-Alike Expansion Component
const LookAlikeExpansion: React.FC<{
  baseAudience: number;
  expansionMultiplier: number;
}> = ({ baseAudience, expansionMultiplier }) => {
  const { t } = useTranslation();
  const expandedAudience = Math.round((baseAudience * expansionMultiplier) / 100) * 100;
  const expansionPercentage = ((expansionMultiplier - 1) * 100);

  const formatNumber = (num: number) => {
    if (num < 100) return '<100';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toLocaleString();
  };

  return (
    <div className="lookalike-expansion">
      <div className="lookalike-header">
        <div className="lookalike-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="lookalike-title">
          <h4>{t('rm.lookAlike')}</h4>
          <span className="lookalike-subtitle">{t('rm.lookAlikeSubtitle')}</span>
        </div>
        <div className="lookalike-multiplier">
          <span className="multiplier-value">{expansionMultiplier.toFixed(1)}x</span>
        </div>
      </div>
      <div className="lookalike-bar-container">
        <div className="lookalike-bar">
          <div
            className="lookalike-bar-base"
            style={{ width: `${100 / expansionMultiplier}%` }}
          >
            <span className="bar-label">{t('rm.base')}</span>
          </div>
          <div
            className="lookalike-bar-expansion"
            style={{ width: `${100 - (100 / expansionMultiplier)}%` }}
          >
            <span className="bar-label">+{expansionPercentage.toFixed(0)}%</span>
          </div>
        </div>
        <div className="lookalike-values">
          <span>{formatNumber(baseAudience)}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
          <span className="expanded-value">{formatNumber(expandedAudience)}</span>
        </div>
      </div>
    </div>
  );
};

const RemarketingListsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser, isAllClients } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  const [lists, setLists] = useState<RemarketingList[]>([]);
  const [activities, setActivities] = useState<ConversionActivity[]>([]);
  const [selectedList, setSelectedList] = useState<RemarketingList | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [lifeSpan, setLifeSpan] = useState('30');
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Capacity estimation from API
  const [capacityData, setCapacityData] = useState<{
    unique_users: number;
    total_events: number;
    daily_rate: number;
    projected_capacity: number;
  } | null>(null);
  const [loadingCapacity, setLoadingCapacity] = useState(false);

  // Fetch capacity estimate when activities or lifeSpan change
  useEffect(() => {
    const fetchCapacity = async () => {
      if (selectedActivities.length === 0) {
        setCapacityData(null);
        return;
      }

      setLoadingCapacity(true);
      try {
        const response = await api.get(
          `/api/remarketing-lists/estimate-capacity`,
          { params: { goal_ids: selectedActivities.join(','), life_span: parseInt(lifeSpan) } }
        );
        setCapacityData(response.data);
      } catch (err) {
        console.error('Error fetching capacity:', err);
        setCapacityData(null);
      } finally {
        setLoadingCapacity(false);
      }
    };

    // Debounce the API call
    const timer = setTimeout(fetchCapacity, 300);
    return () => clearTimeout(timer);
  }, [selectedActivities, lifeSpan]);

  // For existing lists, also fetch capacity based on their activities
  useEffect(() => {
    if (selectedList && selectedActivities.length > 0) {
      const fetchCapacity = async () => {
        setLoadingCapacity(true);
        try {
          const response = await api.get(
            `/api/remarketing-lists/estimate-capacity`,
            { params: { goal_ids: selectedActivities.join(','), life_span: parseInt(lifeSpan) } }
          );
          setCapacityData(response.data);
        } catch (err) {
          console.error('Error fetching capacity:', err);
        } finally {
          setLoadingCapacity(false);
        }
      };
      fetchCapacity();
    }
  }, [selectedList?.id]);

  // Estimated capacity - use real data from API
  const estimatedCapacity = useMemo(() => {
    // Always prefer API data if available
    if (capacityData?.projected_capacity) {
      return capacityData.projected_capacity;
    }
    // Fallback to list_size for existing lists
    if (selectedList) {
      return selectedList.list_size || 0;
    }
    return 0;
  }, [selectedList, capacityData]);

  // Look-alike expansion multiplier (typically 3-10x based on audience quality)
  const expansionMultiplier = useMemo(() => {
    const baseMultiplier = 5; // Average look-alike expansion
    // Smaller, more defined audiences expand better
    if (estimatedCapacity < 1000) return baseMultiplier * 1.5;
    if (estimatedCapacity < 5000) return baseMultiplier * 1.2;
    if (estimatedCapacity < 20000) return baseMultiplier;
    return baseMultiplier * 0.8;
  }, [estimatedCapacity]);

  useEffect(() => {
    if (selectedAdvertiserId) {
      loadLists();
      loadActivities();
      setSelectedList(null);
      setIsCreating(false);
    }
  }, [selectedAdvertiserId]);

  const loadLists = useCallback(async () => {
    if (!selectedAdvertiserId) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/api/remarketing-lists/${selectedAdvertiserId}`
      );
      setLists(response.data);
    } catch (err) {
      console.error('Error loading remarketing lists:', err);
      setLists([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAdvertiserId]);

  const loadActivities = useCallback(async () => {
    if (!selectedAdvertiserId) return;
    try {
      const response = await api.get(
        `/api/conversions/${selectedAdvertiserId}/activities`
      );
      setActivities(response.data);
    } catch (err) {
      console.error('Error loading activities:', err);
      setActivities([]);
    }
  }, [selectedAdvertiserId]);

  const handleCreateNew = () => {
    setSelectedList(null);
    setIsCreating(true);
    setListName('');
    setListDescription('');
    setLifeSpan('30');
    setSelectedActivities([]);
    setError('');
    setSuccess('');
  };

  const handleSelectList = (list: RemarketingList) => {
    setSelectedList(list);
    setIsCreating(false);
    setListName(list.name);
    setListDescription(list.description || '');
    setLifeSpan(list.life_span.toString());
    // Use activity_ids array from API, fallback to single activity_id
    const activityIds = list.activity_ids
      ? list.activity_ids
      : (list.activity_id ? [list.activity_id] : []);
    setSelectedActivities(activityIds);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!listName.trim()) {
      setError(t('rm.nameRequired'));
      return;
    }
    if (selectedActivities.length === 0) {
      setError(t('rm.sourceRequired'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        advertiser_id: selectedAdvertiserId,
        name: listName.trim(),
        description: listDescription.trim(),
        life_span: parseInt(lifeSpan, 10),
        activity_ids: selectedActivities,
      };

      if (isCreating) {
        await api.post(`/api/remarketing-lists`, payload);
        setSuccess(t('rm.listCreated'));
      } else if (selectedList) {
        await api.patch(
          `/api/remarketing-lists/${selectedList.id}`,
          payload
        );
        setSuccess(t('rm.listUpdated'));
      }

      await loadLists();
      setIsCreating(false);
    } catch (err: any) {
      console.error('Error saving remarketing list:', err);
      setError(err.response?.data?.detail || t('rm.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedList) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/remarketing-lists/${selectedList.id}`);
      setSuccess(t('rm.listDeleted'));
      setShowDeleteModal(false);
      setSelectedList(null);
      await loadLists();
    } catch (err: any) {
      console.error('Error deleting list:', err);
      setError(err.response?.data?.detail || t('rm.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setSelectedList(null);
    setIsCreating(false);
    setListName('');
    setListDescription('');
    setLifeSpan('30');
    setSelectedActivities([]);
    setError('');
    setSuccess('');
  };

  const selectActivity = (activityId: string) => {
    // Single select - CM360 supports only one activity per list
    setSelectedActivities(prev =>
      prev.includes(activityId) ? [] : [activityId]
    );
  };

  // Esc key handler for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDeleteModal && !isDeleting) {
        setShowDeleteModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, isDeleting]);

  const filteredLists = lists.filter((list) =>
    list.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatNumber = (num: number) => {
    if (num < 100 && num > 0) return '<100';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.remarketing" />;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('rm.title')}</h1>
          <p className="header-subtitle">{t('rm.subtitle')}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="url-lists-layout">
        {/* Left Panel - Lists */}
        <aside className="url-lists-panel">
          {/* Search */}
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('rm.searchLists')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Add Button */}
          {!isAnalyst && (
          <button className="add-list-btn" onClick={handleCreateNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{t('rm.addList')}</span>
          </button>
          )}

          {/* Lists */}
          <div className="lists-container">
            {loading ? (
              <div className="lists-loading">
                <div className="spinner"></div>
                <span>{t('rm.loadingLists')}</span>
              </div>
            ) : filteredLists.length === 0 ? (
              <div className="lists-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{searchQuery ? t('rm.noMatching') : t('rm.noLists')}</span>
              </div>
            ) : (
              filteredLists.map(list => (
                <div
                  key={list.id}
                  className={`list-item ${selectedList?.id === list.id ? 'active' : ''}`}
                  onClick={() => handleSelectList(list)}
                >
                  <div className="list-item-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-name" style={list.is_locked ? { color: '#888' } : undefined}>
                      {list.is_locked && (
                        <span title={t('rm.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                          </svg>
                        </span>
                      )}
                      {list.name}
                    </div>
                    <div className="list-item-meta">
                      {t('rm.listMeta', {
                        users: formatNumber((list as any).estimated_capacity || 0),
                        days: list.life_span,
                      })}
                      {!list.active && <span className="status-badge paused">{t('rm.paused')}</span>}
                    </div>
                  </div>
                  <svg className="list-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Right Panel - Editor */}
        <div className="url-editor-panel">
          {!selectedList && !isCreating ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{t('rm.selectList')}</h3>
              <p>{t('rm.chooseListHint')}</p>
              {!isAnalyst && (
              <button className="editor-empty-btn" onClick={handleCreateNew}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('rm.createNewList')}
              </button>
              )}
            </div>
          ) : (
            <div className="editor-content">
              {/* Editor Header */}
              <div className="editor-header">
                <h2>{isCreating ? t('rm.newList') : t('rm.editList')}</h2>
              </div>

              {/* Alerts */}
              {error && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                  <button onClick={() => setError('')}>&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"></path>
                    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"></polyline>
                  </svg>
                  <span>{success}</span>
                  <button onClick={() => setSuccess('')}>&times;</button>
                </div>
              )}

              {/* Form - Two Column Layout */}
              <div className="remarketing-form-grid">
                {/* Left Column - Form Fields */}
                <div className="remarketing-form-fields">
                  <div className="form-group">
                    <label className="form-label">{t('rm.listName')}</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('rm.listNamePlaceholder')}
                      value={listName}
                      onChange={(e) => setListName(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('rm.audienceSource')}</label>
                    {activities.length === 0 ? (
                      <div className="form-empty-state">{t('rm.noGoals')}</div>
                    ) : (
                      <div className="targeting-tags" style={{ flexWrap: 'wrap', gap: '8px' }}>
                        {activities.map((activity) => (
                          <span
                            key={activity.id}
                            className={`targeting-tag targeting-tag-toggle ${selectedActivities.includes(activity.id) ? 'active' : ''}`}
                            onClick={() => selectActivity(activity.id)}
                          >
                            {activity.name}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="form-help-text">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                      <span>{t('rm.audienceSourceHint')}</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('rm.membershipDuration')}</label>
                    <div className="duration-input-group">
                      <input
                        type="number"
                        className="form-input duration-input"
                        value={lifeSpan}
                        onChange={(e) => setLifeSpan(e.target.value)}
                        min="1"
                        max="540"
                      />
                      <span className="duration-suffix">{t('rm.days')}</span>
                      <div className="duration-presets">
                        {[7, 14, 30, 60, 90].map(days => (
                          <button
                            key={days}
                            className={`duration-preset ${parseInt(lifeSpan) === days ? 'active' : ''}`}
                            onClick={() => setLifeSpan(days.toString())}
                            type="button"
                          >
                            {days}d
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Status for existing lists */}
                  {selectedList && (
                    <div className={`list-status-card ${selectedList.active ? 'is-active' : 'is-paused'}`}>
                      <div className="status-badge">
                        <span className="status-pulse"></span>
                        <span className="status-label">{selectedList.active ? t('rm.live') : t('rm.paused')}</span>
                      </div>
                      <div className="status-details">
                        <div className="status-detail-item">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          <span>{new Date(selectedList.created_at).toLocaleDateString(currentLocale(), { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Capacity & Insights */}
                <div className="remarketing-insights">
                  {/* Capacity Gauge */}
                  <div className="insight-card">
                    <div className="insight-card-header">
                      <h3>{t('rm.estimatedCapacity')}</h3>
                      {selectedActivities.length === 0 && !selectedList && (
                        <span className="insight-hint">{t('rm.selectSourcesHint')}</span>
                      )}
                      {loadingCapacity && (
                        <span className="insight-hint">{t('rm.calculating')}</span>
                      )}
                    </div>
                    {(selectedActivities.length > 0 || selectedList) && estimatedCapacity > 0 ? (
                      <>
                        <CapacityGauge
                          estimatedUsers={estimatedCapacity}
                          maxCapacity={50000}
                          lifeSpan={parseInt(lifeSpan)}
                        />
                        {/* Real data metrics */}
                        {capacityData && (
                          <div className="capacity-real-data">
                            <div className="real-data-item">
                              <span className="real-data-value">
                                {capacityData.unique_users < 100 ? '<100' : capacityData.unique_users}
                              </span>
                              <span className="real-data-label">{t('rm.users')}</span>
                            </div>
                            <div className="real-data-item">
                              <span className="real-data-value">
                                {capacityData.total_events < 100 ? '<100' : capacityData.total_events}
                              </span>
                              <span className="real-data-label">{t('rm.events')}</span>
                            </div>
                            <div className="real-data-item">
                              <span className="real-data-value">
                                {capacityData.daily_rate < 30 ? '<30' : capacityData.daily_rate}
                              </span>
                              <span className="real-data-label">{t('rm.daily')}</span>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="insight-placeholder">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                        <span>{t('rm.selectSourcesPlaceholder')}</span>
                      </div>
                    )}
                  </div>

                  {/* Look-Alike Expansion */}
                  {(selectedActivities.length > 0 || selectedList) && estimatedCapacity > 0 && (
                    <div className="insight-card">
                      <LookAlikeExpansion
                        baseAudience={estimatedCapacity}
                        expansionMultiplier={expansionMultiplier}
                      />
                    </div>
                  )}

                  {/* Audience Quality Tips */}
                  {(selectedActivities.length > 0 || selectedList) && (
                    <div className="insight-tips">
                      <div className="tip-item">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        <span>
                          {parseInt(lifeSpan) >= 30
                            ? t('rm.tipGoodRetention')
                            : t('rm.tipLongerRetention')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="editor-actions">
                <div className="editor-actions-left">
                  {!isAnalyst && !isCreating && selectedList && !selectedList.is_locked && (
                    <button
                      className="btn-danger"
                      onClick={() => setShowDeleteModal(true)}
                      disabled={saving}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      {t('rm.delete')}
                    </button>
                  )}
                </div>
                {!isAnalyst && !selectedList?.is_locked && (
                <div className="editor-actions-right">
                  <button
                    className="btn-secondary"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    {t('rm.cancel')}
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={saving || !listName.trim() || selectedActivities.length === 0}
                  >
                    {saving ? (
                      <>
                        <div className="btn-spinner"></div>
                        {t('rm.saving')}
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                          <polyline points="17 21 17 13 7 13 7 21"></polyline>
                          <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        {isCreating ? t('rm.createList') : t('rm.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('rm.deleteListQ')}</h3>
            </div>
            <div className="modal-body">
              <p>
                <Trans i18nKey="rm.deleteConfirm" values={{ name: selectedList?.name }}
                  components={{ b: <strong /> }} />{' '}
                {t('rm.cannotBeUndone')}
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('rm.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? t('rm.deleting') : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RemarketingListsPage;
